import React, {useCallback, useEffect, useState} from 'react';
import {useLocation, useNavigate} from "react-router";
import Modal from "../../components/Modal";
import {useTranslation} from 'react-i18next';
import {AxiosError} from 'axios';
import {Location} from 'history';
import client from 'lib/CommonApi/client';
import stringUtils from 'common/stringUtils';
import {
	callAlertWeb,
	callNativeWeb,
	footerShowEventWeb,
	openMapWeb,
} from 'common/appInterface';
import {CardProductCode} from "../../constants/Card";
import {SuccessResponseCodeList} from "../../constants/Common";
import { useLoading } from 'components/Loading/LoadingProvider';

const DeliveryBranchPage: React.FC = () => {
  /** Location State */
  const location = useLocation() as Location<LocationState>;
  /** Navigate */
  const navigate = useNavigate();
  /** 다국어처리 */
  const { t, i18n } = useTranslation();

  const { showLoading, hideLoading } = useLoading();

  const screenId = "APPL12";
  const screenName = t("APPL12.title");

  const [cardApply] = useState<Partial<CardApplyStep6>>(location.state.apply);
  const [branchList, setBranchList] = useState<ManagementBranch[]>([]);
  const [branchIndex, setBranchIndex] = useState<number>();
  const [branch, setBranch] = useState("");
  const [isModal, setModal] = useState(false);

  const [deliveryFee, setDeliveryFee] = useState<number | null>(null);
  const [displayCardIssueFee, setDisplayCardIssueFee] = useState<number | null>(null);
	
  /** 뒤로가기 처리 (backKeyEvent_App (APP > WEB)) */
  const callbackBackkeyEvent = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  /** 모달 닫기 */
  const closeModal = useCallback(() => {
    setModal(false);
  }, []);
  
  /** 관리영업점 조회 */
  const callBranchList = useCallback(async () => {
    showLoading();
    await client
      .get('/api/app/apply/branch', { params: location.state.query })
      .then((response) => {
        let filterBranchList: ManagementBranch[] = [];
        // 본점 제외처리
        (response.data.response as ManagementBranch[]).forEach((value) => {
          if (value.code !== '0930001') {
            filterBranchList.push(value);
          }
        });
        setBranchList(filterBranchList);
      })
      .catch((e: AxiosError) => {
        callAlertWeb('ERROR', '99', e.message);
      })
      .finally(() => {
        hideLoading();
      });
  }, [location, showLoading, hideLoading]);

  /**
   * dhbyun
   * 카드 배송 수수료 조회
   */
  const callDeliveryFee = useCallback(
    async () => {
      await client.post(`/api/app/apply/branch/deliveryFee`, { ...location.state.query, receiveSeq: cardApply.receiveSeq, receiveDate: cardApply.receiveDate }).then((response) => {
        if(response.data.response) {
          setDeliveryFee(response.data.response.deliveryFee);
        }
      }).catch((e: AxiosError) => {
        callAlertWeb("ERROR", "99", e.message);
      });
    },
    [location, cardApply]
  );
	
  /**
   * dhbyun
   * 표기 카드 발급비 조회
   */
  const inquiryDisplayCardIssueFeeApi = useCallback(
	  async () => {
		  await client.get(`/api/app/apply/${cardApply.cardProductCode}/issueFee`, { params: location.state.query }).then((response) => {
			  if(response.data.response) {
				  setDisplayCardIssueFee(response.data.response.cardIssueFee);
			  }
		  }).catch((e: AxiosError) => {
			  callAlertWeb("ERROR", "99", e.message);
		  });
		  },
	  [cardApply.cardProductCode, location]
  );
  

  /** Next 처리 */
  const clickNext = useCallback(() => {
    setModal(true);
  }, []);
	
	/** 카드신청정보 확인완료 */
	const clickModalYes = useCallback(() => {
    showLoading();

    // 카드신청내역 등록
    client
      .patch('/api/app/apply/history', {
        ...location.state.query,
        receiveSeq: cardApply.receiveSeq,
        receiveDate: cardApply.receiveDate,
        receiveStatusCode: '07',
        sendTypeDivisionCode: '3',
        deliverySelectCode: cardApply.deliverySelectCode,
        thirdPartyReceiptYn: 'Y',
        deliveryBranchCode: branch,
        cardProductCode: cardApply.cardProductCode,
        addressChangeYn: cardApply.isChangeDelivery ? 'Y' : 'N'
      })
      .then(async (response) => {
        if (response.data.code === '00') {
          // 수령지변경은 메인으로 이동
          if (cardApply.isChangeDelivery) {
            navigate('/main', {
              state: {
                query: location.state.query,
                swiperIndex: location.state.swiperIndex,
                tabIndex: location.state.tabIndex
              }
            });
          } else {
            switch (Number(cardApply.cardProductCode)) {
              case CardProductCode.DreamCard:
              case CardProductCode.PremierCard:
                navigate('/apply/step7', {
                  state: {
                    query: location.state.query,
                    apply: {
                      isChangeDelivery: false,
                      callPage: 'cardApply',
                      cardApply: cardApply
                    }
                  }
                });
                break;
              case CardProductCode.WelcomeCard:
                if (
                  cardApply.requestIssueDivisionCode?.startsWith('2') ||
                  cardApply.requestIssueDivisionCode?.startsWith('3')
                ) {
                  navigate('/apply/complet', {
                    state: { ...location.state, apply: cardApply }
                  });
                } else {
                  await client
                    .post('/api/app/apply/chargeCardIssueFee', {
                      ...location.state.query,
                      receiveSeq: cardApply.receiveSeq,
                      receiveDate: cardApply.receiveDate
                    })
                    .then((res) => {
                      if (SuccessResponseCodeList.includes(res.data.code)) {
                        if (res.data.response.bCharged) {
                          navigate('/apply/complet', {
                            state: { ...location.state, apply: cardApply }
                          });
                        } else {
                          navigate('/apply/depositCardIssuanceFee', {
                            state: {
                              ...location.state,
                              apply: cardApply,
                              storage: {
                                ...location.state.storage,
                                chargeCardIssueFeeRes: res.data.response,
                                displayCardIssueFee: displayCardIssueFee
                              }
                            }
                          });
                        }
                      } else {
                        callAlertWeb('ERROR', res.data.code, res.data.message);
                      }
                    })
                    .catch((e: AxiosError) => {
                      callAlertWeb('ERROR', '99', e.message);
                    });
                }
                break;
              case CardProductCode.DirectCard:
              default:
                navigate('/apply/complet', {
                  state: { ...location.state, apply: cardApply }
                });
                break;
            }
          }
        }
      })
      .catch((e: AxiosError) => {
        callAlertWeb('ERROR', '99', e.message);
      })
      .finally(() => {
        hideLoading();
      });
  }, [
    navigate,
    location,
    cardApply,
    branch,
    displayCardIssueFee,
    showLoading,
    hideLoading
  ]);

  /** 구글지도 호출 (openMap_Web (APP > WEB)) */
  const clickOpenMap = useCallback((address: string) => {
    openMapWeb(address);
  }, []);

  // 최초실행
  useEffect(() => {
    // 언어설정
    i18n.changeLanguage(location.state.query.languageCode);
    // callNative_Web 처리 (WEB > APP)
    callNativeWeb(screenId, screenName);
    // 하단바 숨기기
    footerShowEventWeb("N");

    console.log("QUERY STATE");
    console.log(location.state);
    console.log(cardApply);

    callBranchList();
    callDeliveryFee();
	inquiryDisplayCardIssueFeeApi();
    // 최초한번실행 useEffect() Warning 제거
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 뒤로가기 이벤트 리스너 (backKeyEvent_App (APP > WEB))
  useEffect(() => {
    document.addEventListener("backpress", callbackBackkeyEvent);
    return () => document.removeEventListener("backpress", callbackBackkeyEvent);
  }, [callbackBackkeyEvent]);

  return (
    <div id="wrap">
      <div id="container" className="pcHidden">
        <section className="contents listtyp01">
          <div className="inner">
            <section className="manage">
              <p className="tit">{t("APPL12.explan01")}</p>
              <div style={{ paddingBottom: 20, fontSize: 15, fontWeight: 500, color: '#e86c43'}}>
                <p>{t(`APPL12.notice01`)}</p>
                <p>{t(`APPL12.notice02`).replace('{0}', stringUtils.nvlDefault(stringUtils.moneyFormat(deliveryFee)))}</p>
              </div>
              {branchList.map((value, index) =>
                <div className={branch === value.code ? "listbox on" : "listbox"} key={value.code}>
                  <div className="radioBox">
                    <input type="radio" id={`address_${value.code}`} name="address" value={value.code} onChange={(e) => { setBranch(e.target.value); setBranchIndex(index); }} />
                    <label htmlFor={`address_${value.code}`}>
                      <span>
                        <p>{t(`BRANCH.name.${value.code}`)}</p>
                        <p className="info">{t(`BRANCH.addr.${value.code}`)} {t(`BRANCH.addrDtl.${value.code}`)}</p>
                      </span>
                    </label>
                  </div>
                </div>
              )}
              {!stringUtils.checkValueNull(branch) && branchList.filter((value) => value.code === branch).map((value) => 
                <div className="map" key={value.code}>
                  <img src={`/assets/images/content/google-map_${value.code}.jpg`} alt="지도" onClick={() => clickOpenMap(value.addr + " " + value.addrDtl)} />
                </div>
              )}
            </section>
          </div>
        </section>
        <div className="btnArea">
          <div className="btnWrapTy01">
            <button type="button" className="btnPrimaryTy01" disabled={stringUtils.checkValueNull(branch)} onClick={clickNext}><span>{t("COMMON.button.next")}</span></button>
          </div>
        </div>
      </div>
      <Modal isOpen={isModal} contentLabel="modal" onRequestClose={closeModal} className="popTyLayer2" overlayClassName="dim" closeTimeoutMS={500}>
        <p className="message">{t("APPL12.explan03")}</p>
        <dl>
          {/*<dt>{t("APPL12.header01")}</dt>
          <dd>{cardApply.userEngName}</dd>*/}
          <dt>{t("APPL12.header02")}</dt>
          <dd>{t("APPL12.header03")} : <span>{t(`BRANCH.name.${branchList[branchIndex!]?.code}`)}</span></dd>
          <dd className="detail">({branchList[branchIndex!]?.zipCode}) {t(`BRANCH.addr.${branchList[branchIndex!]?.code}`)} {t(`BRANCH.addrDtl.${branchList[branchIndex!]?.code}`)}</dd>
        </dl>
        <div className="btnWrapTy02">
          <button type="button" className="btnPrimaryTy03" onClick={closeModal}><span>{t("COMMON.button.no")}</span></button>
          <button type="button" className="btnPrimaryTy01" onClick={clickModalYes}><span>{t("COMMON.button.yes")}</span></button>
        </div>
      </Modal>
    </div>
  );
};

export default DeliveryBranchPage;